export default function ChallengesSection() {
  const challenges = [
    {
      icon: 'ri-time-fill',
      title: 'Time-Consuming Traditional Sales',
      problem: 'Traditional sales of tenant occupied properties can take 6-12 months. You need to coordinate showings around tenant schedules, which limits buyer interest.',
      solution: 'We buy rental property as-is with tenants in place. No showings, no waiting. Close in 7-14 days.'
    },
    {
      icon: 'ri-user-unfollow-fill',
      title: 'Difficult or Non-Paying Tenants',
      problem: 'Problem tenants make properties nearly impossible to sell traditionally. Buyers want vacant properties or perfect tenants.',
      solution: 'We buy properties with any tenant situation—even difficult tenants or non-paying occupants. We handle the situation after closing.'
    },
    {
      icon: 'ri-tools-fill',
      title: 'Deferred Maintenance Issues',
      problem: 'Rental properties often need repairs. Traditional buyers demand repairs or price reductions, and you can\'t easily make repairs with tenants living there.',
      solution: 'We buy rental property in any condition. No repairs required, no matter how much work is needed.'
    },
    {
      icon: 'ri-file-damage-fill',
      title: 'Complex Lease Agreements',
      problem: 'Multiple leases with different terms, expiration dates, and conditions complicate traditional sales. Buyers get nervous about inheriting lease obligations.',
      solution: 'We have extensive experience with complex lease situations. We review all leases and make offers that account for existing agreements.'
    },
    {
      icon: 'ri-money-dollar-circle-fill',
      title: 'Lost Rental Income',
      problem: 'Traditional sales often require properties to be vacant, meaning you lose months of rental income while marketing and showing the property.',
      solution: 'Keep collecting rent until closing day. Your tenants stay in place, and you maintain cash flow throughout the sale process.'
    },
    {
      icon: 'ri-emotion-unhappy-fill',
      title: 'Tenant Cooperation Problems',
      problem: 'Tenants may not cooperate with showings, may not keep the property presentable, or may actively discourage buyers.',
      solution: 'Minimal tenant disruption. We typically need just one inspection, and we work around tenant schedules respectfully.'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Common Challenges When Selling Tenant Occupied Property
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Selling rental property with tenants presents unique challenges. Here's how we solve the most common problems landlords face.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {challenges.map((challenge, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
            >
              <div className="p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${challenge.icon} text-red-600 text-3xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{challenge.title}</h3>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                    <div className="flex items-start space-x-2">
                      <i className="ri-close-circle-fill text-red-500 text-xl flex-shrink-0 mt-1"></i>
                      <div>
                        <div className="font-semibold text-red-900 mb-1">The Problem:</div>
                        <p className="text-red-800 text-sm">{challenge.problem}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-teal-50 border-l-4 border-teal-500 p-4 rounded">
                    <div className="flex items-start space-x-2">
                      <i className="ri-check-double-fill text-teal-500 text-xl flex-shrink-0 mt-1"></i>
                      <div>
                        <div className="font-semibold text-teal-900 mb-1">Our Solution:</div>
                        <p className="text-teal-800 text-sm">{challenge.solution}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">Facing Any of These Challenges?</h3>
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
              We've helped hundreds of landlords overcome these exact problems. Let us show you how easy selling tenant occupied property can be.
            </p>
            <a 
              href="tel:+1-915-206-5814"
              className="inline-flex items-center bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-bold transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill mr-2"></i>
              Call (915) 206-5814 Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}